import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Link, Grid, Column, Button } from '@carbon/react';
import {
  LogoTwitter,
  LogoLinkedin,
  LogoGithub,
  Email,
  Location,
} from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import logo from '../assets/logo.webp';

const Footer = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const bgColor = theme === 'dark' ? '#0d0d0d' : '#e0e0e0';
  const textColor = theme === 'dark' ? '#c6c6c6' : '#525252';
  const headingColor = theme === 'dark' ? '#f4f4f4' : '#161616';
  const borderColor = theme === 'dark' ? '#393939' : '#c6c6c6';

  return (
    <footer
      style={{
        backgroundColor: bgColor,
        borderTop: `1px solid ${borderColor}`,
        padding: '48px 0 24px',
      }}
      data-testid="main-footer"
    >
      <Grid>
        <Column lg={4} md={4} sm={4}>
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <img
                src={logo}
                alt="ChillVerify Logo"
                style={{ width: '24px', height: '24px', borderRadius: '4px' }}
              />
              <h4 className="app-brand" style={{ color: headingColor, margin: 0, fontSize: '1.25rem' }}>
                ChillVerify
              </h4>
            </div>
            <p style={{ color: textColor, fontSize: '0.875rem', lineHeight: 1.6 }}>
              Comprehensive platform for sensor analytics, monitoring, and data visualization.
              Track temperature and humidity across logistics, storage, and operations.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '16px' }}>
            <Button kind="ghost" size="sm" hasIconOnly renderIcon={LogoTwitter} iconDescription="Twitter" data-testid="footer-twitter" />
            <Button kind="ghost" size="sm" hasIconOnly renderIcon={LogoLinkedin} iconDescription="LinkedIn" data-testid="footer-linkedin" />
            <Button kind="ghost" size="sm" hasIconOnly renderIcon={LogoGithub} iconDescription="GitHub" data-testid="footer-github" />
          </div>
        </Column>

        <Column lg={3} md={2} sm={4}>
          <h5 style={{ color: headingColor, marginBottom: '16px', fontSize: '1rem', fontWeight: 600 }}>Product</h5>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {['Features', 'Sensors', 'Reports', 'Shop'].map((item) => (
              <li key={item} style={{ marginBottom: '12px' }}>
                <Link
                  href={`/${item.toLowerCase()}`}
                  onClick={(e) => { e.preventDefault(); navigate(`/${item.toLowerCase()}`); }}
                  style={{ color: textColor, textDecoration: 'none' }}
                  data-testid={`footer-link-${item.toLowerCase()}`}
                >
                  {item}
                </Link>
              </li>
            ))}
          </ul>
        </Column>

        <Column lg={3} md={2} sm={4}>
          <h5 style={{ color: headingColor, marginBottom: '16px', fontSize: '1rem', fontWeight: 600 }}>Company</h5>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            <li style={{ marginBottom: '12px' }}>
              <Link
                href="/support"
                onClick={(e) => { e.preventDefault(); navigate('/support'); }}
                style={{ color: textColor, textDecoration: 'none' }}
                data-testid="footer-link-support"
              >
                Help
              </Link>
            </li>
            <li style={{ marginBottom: '12px' }}>
              <Link
                href="/contact"
                onClick={(e) => { e.preventDefault(); navigate('/contact'); }}
                style={{ color: textColor, textDecoration: 'none' }}
                data-testid="footer-link-contact"
              >
                Contact
              </Link>
            </li>
            <li style={{ marginBottom: '12px' }}>
              <Link
                href="/privacy"
                onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}
                style={{ color: textColor, textDecoration: 'none' }}
                data-testid="footer-link-privacy-policy"
              >
                Privacy Policy
              </Link>
            </li>
            <li style={{ marginBottom: '12px' }}>
              <Link
                href="/terms"
                onClick={(e) => { e.preventDefault(); navigate('/terms'); }}
                style={{ color: textColor, textDecoration: 'none' }}
                data-testid="footer-link-terms-of-service"
              >
                Terms of Service
              </Link>
            </li>
            <li style={{ marginBottom: '12px' }}>
              <Link
                href="/refund-policy"
                onClick={(e) => { e.preventDefault(); navigate('/refund-policy'); }}
                style={{ color: textColor, textDecoration: 'none' }}
                data-testid="footer-link-refund-policy"
              >
                Refund Policy
              </Link>
            </li>
            <li style={{ marginBottom: '12px' }}>
              <Link
                href="/return-policy"
                onClick={(e) => { e.preventDefault(); navigate('/return-policy'); }}
                style={{ color: textColor, textDecoration: 'none' }}
                data-testid="footer-link-return-policy"
              >
                Return Policy
              </Link>
            </li>
          </ul>
        </Column>

        <Column lg={3} md={4} sm={4}>
          <h5 style={{ color: headingColor, marginBottom: '16px', fontSize: '1rem', fontWeight: 600 }}>Contact</h5>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: textColor }}>
              <Email size={16} /> support@chillverify.com
            </li>
            <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: textColor }}>
              <Location size={16} /> Devonport, Tasmania, Australia
            </li>
          </ul>
        </Column>

        <Column lg={3} md={4} sm={4}>
          <h5 style={{ color: headingColor, marginBottom: '16px', fontSize: '1rem', fontWeight: 600 }}>Get Started</h5>
          <Button kind="primary" size="md" onClick={() => navigate('/signup')} data-testid="footer-signup-btn">
            Sign Up Free
          </Button>
          {/* Removed \"No credit card required\" copy per latest requirements */}
        </Column>
      </Grid>

      <Grid style={{ marginTop: '48px', paddingTop: '24px', borderTop: `1px solid ${borderColor}` }}>
        <Column lg={16} md={8} sm={4}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <p style={{ color: textColor, fontSize: '0.875rem', margin: 0 }}>
              © 2026 ChillVerify · Sense Stack ABN 14 187 234 379. All rights reserved.
            </p>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <Link 
                href="/privacy" 
                onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}
                style={{ color: textColor, fontSize: '0.875rem' }}
              >
                Privacy
              </Link>
              <Link 
                href="/terms" 
                onClick={(e) => { e.preventDefault(); navigate('/terms'); }}
                style={{ color: textColor, fontSize: '0.875rem' }}
              >
                Terms
              </Link>
              <Link 
                href="/refund-policy" 
                onClick={(e) => { e.preventDefault(); navigate('/refund-policy'); }}
                style={{ color: textColor, fontSize: '0.875rem' }}
              >
                Refund Policy
              </Link>
              <Link 
                href="/return-policy" 
                onClick={(e) => { e.preventDefault(); navigate('/return-policy'); }}
                style={{ color: textColor, fontSize: '0.875rem' }}
              >
                Return Policy
              </Link>
            </div>
          </div>
        </Column>
      </Grid>
    </footer>
  );
};

export default Footer;
